function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { Button } from '@naf/button';
import { Text } from '@naf/text';
import { spacing, themeLight } from '@naf/theme';
import { useToast, ToastVariant } from '@naf/toast';
import { AddCircleOutline } from '@styled-icons/material/AddCircleOutline';
import styled, { css } from 'styled-components';
import { propValidationConfig } from '../../propValidationConfig';
export const fileTypeRegex = /(image|application)\/(png|jpg|jpeg|pdf)/gm;
export const FileDrop = /*#__PURE__*/forwardRef(({
  disabled = false,
  error = false,
  onChange,
  illustration,
  name,
  files,
  setFiles,
  maxFiles,
  ...rest
}, ref) => {
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);
  const {
    showToast,
    Toast
  } = useToast();

  // Register file in form state
  useEffect(() => {
    onChange(files);
  }, [files, onChange]);
  const handleDrag = e => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  // Max request size is 10MB
  const validateSize = (uploadedFiles, newFiles) => {
    let size = 0;
    uploadedFiles.forEach(file => {
      size += file.size;
    });
    newFiles.forEach(file => {
      size += file.size;
    });
    if (maxFiles && uploadedFiles.length + newFiles.length > maxFiles) {
      showToast({
        variant: ToastVariant.Error,
        icon: true,
        message: `Du kan ikke laste opp flere enn ${maxFiles} filer`
      });
      return false;
    }
    if (size > 10 * 2 ** 20) {
      showToast({
        variant: ToastVariant.Error,
        icon: true,
        message: 'Du har overskredet grensen på 10MB'
      });
      return false;
    }
    return true;
  };
  const validateType = file => {
    if (file.type.match(fileTypeRegex)) {
      return true;
    }
    showToast({
      variant: ToastVariant.Error,
      icon: true,
      message: 'Ugyldig filtype. Gyldige filtyper er jpeg, jpg, png og pdf'
    });
    return false;
  };
  const uploadFiles = (fileState, selectedFiles) => {
    if (selectedFiles && selectedFiles.length && validateSize(fileState, selectedFiles)) {
      if (fileState.some(fileInArray => selectedFiles.some(selectedFile => fileInArray.name === selectedFile.name))) {
        showToast({
          variant: ToastVariant.Information,
          icon: true,
          message: 'Du har allerede lastet opp en eller flere av filene'
        });
        const filteredFiles = selectedFiles.filter(selectedFile => validateType(selectedFile) && !fileState.some(fileInArray => selectedFile.name === fileInArray.name));
        setFiles([...fileState, ...filteredFiles]);
      } else {
        const filteredFiles = selectedFiles.filter(selectedFile => validateType(selectedFile));
        setFiles([...fileState, ...filteredFiles]);
      }
    }
  };
  const handleDrop = e => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer) {
      const fileState = [...files];
      const selectedFiles = [...e.dataTransfer.files];
      uploadFiles(fileState, selectedFiles);
    }
  };
  const handleChange = e => {
    e.preventDefault();
    e.stopPropagation();
    if (e.target && e.target.files) {
      const fileState = [...files];
      const selectedFiles = [...e.target.files];
      uploadFiles(fileState, selectedFiles);
    }
  };
  const handleButtonClick = e => {
    e.preventDefault();
    if (inputRef && inputRef.current) {
      inputRef.current.click();
    }
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Input, {
    type: "file",
    name: name,
    multiple: true,
    id: "input-file-upload",
    onChange: handleChange,
    ref: inputRef,
    disabled: disabled,
    "aria-disabled": disabled
  }), /*#__PURE__*/React.createElement(DropZone, _extends({
    id: "drop-zone",
    active: !disabled && dragActive,
    onDragEnter: !disabled ? handleDrag : undefined,
    htmlFor: "input-file-upload",
    disabled: disabled,
    error: error,
    "aria-dropeffect": "execute",
    "aria-disabled": disabled,
    "aria-label": "file drop",
    ref: ref
  }, rest), dragActive ? /*#__PURE__*/React.createElement(AddCircleOutline, {
    size: 24
  }) : illustration && /*#__PURE__*/React.createElement(Img, {
    src: illustration,
    alt: "Last opp filer illustrasjon"
  }), dragActive ? null : /*#__PURE__*/React.createElement(Button, {
    type: "button",
    variant: "outline",
    onClick: e => !disabled && handleButtonClick(e)
  }, "Finn og last opp fil"), /*#__PURE__*/React.createElement(Text, null, dragActive ? 'Slipp filen for å laste opp' : 'Eller dra og slipp fil her'), dragActive && /*#__PURE__*/React.createElement(Overlay, {
    onDragEnter: !disabled ? handleDrag : undefined,
    onDragOver: !disabled ? handleDrag : undefined,
    onDragLeave: !disabled ? handleDrag : undefined,
    onDrop: !disabled ? handleDrop : undefined
  })), /*#__PURE__*/React.createElement(Toast, null));
});
const Input = styled.input.withConfig({
  displayName: "fileDrop__Input",
  componentId: "sc-1qwqsw-0"
})(["display:none;"]);
const DropZone = styled.label.withConfig(propValidationConfig).withConfig({
  displayName: "fileDrop__DropZone",
  componentId: "sc-1qwqsw-1"
})(["width:100%;height:252px;box-sizing:border-box;text-align:center;position:relative;display:flex;justify-content:center;align-items:center;flex-direction:column;border:2px dashed ", ";background-color:'transparent';cursor:pointer;", " ", " ", " button{margin-bottom:", ";}svg{margin-bottom:", ";}"], ({
  theme
}) => theme.border ? theme.border.default : themeLight.border.default, ({
  active
}) => active && css(["background-color:", ";"], ({
  theme
}) => theme.componentColors ? theme.componentColors.interactiveElement.hover : themeLight.componentColors.interactiveElement.hover), ({
  error
}) => error && css(["border-color:", ";"], ({
  theme
}) => theme.componentColors ? theme.componentColors.alert.error : themeLight.componentColors.alert.error), ({
  disabled
}) => disabled && css(["cursor:not-allowed;*{cursor:not-allowed;color:", ";}"], ({
  theme
}) => theme.componentColors ? theme.componentColors.interactiveElement.disabled : themeLight.componentColors.interactiveElement.disabled), spacing.space12, spacing.space12);
const Overlay = styled.div.withConfig({
  displayName: "fileDrop__Overlay",
  componentId: "sc-1qwqsw-2"
})(["position:absolute;width:100%;height:100%;top:0;right:0;bottom:0;left:0;"]);
const Img = styled.img.withConfig({
  displayName: "fileDrop__Img",
  componentId: "sc-1qwqsw-3"
})(["margin-bottom:", ";height:", ";width:", ";"], spacing.space24, spacing.space80, spacing.space80);
//# sourceMappingURL=fileDrop.js.map